import { useGetLeadSourcesQuery } from "@/redux/rtk/features/leadSource/leadSourceApi";
import { useGetLeadStatusesQuery } from "@/redux/rtk/features/leadStatus/leadStatusApi";
import { useGetInterestsQuery } from "@/redux/rtk/features/interest/interestApi";
import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetUniversitiesQuery } from "@/redux/rtk/features/university/universityApi";
import { useGetCampaignCodesQuery } from "@/redux/rtk/features/campaignCode/campaignCodeApi";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllAgent } from "@/redux/rtk/features/CRM/agent/agentSlice";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NATIONALITIES } from "@/utils/nationalities";
import { COUNTRIES } from "@/utils/countries";
import usePermissions from "@/utils/usePermissions";

export default function useLeadFilter(enabled = false) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { permissions } = usePermissions();
  const canReadAllUser = permissions?.includes("readAll-user");

  const { list: staffList, loading: staffLoading } = useSelector((state) => state.users);
  const { list: agentList, loading: agentLoading } = useSelector((state) => state.agent);
  const { data: leadSources, isLoading: leadSourcesLoading } = useGetLeadSourcesQuery(undefined, { skip: !enabled });
  const { data: leadStatuses, isLoading: leadStatusesLoading } = useGetLeadStatusesQuery(undefined, { skip: !enabled });
  const { data: interests, isLoading: interestsLoading } = useGetInterestsQuery(undefined, { skip: !enabled });
  const { data: majors, isLoading: majorsLoading } = useGetMajorsQuery(undefined, { skip: !enabled });
  const { data: campaignCodes, isLoading: campaignCodesLoading } = useGetCampaignCodesQuery(undefined, { skip: !enabled });
  const { data: universities } = useGetUniversitiesQuery(undefined, { skip: !enabled });

  useEffect(() => {
    if (enabled) {
      dispatch(loadAllStaff({ query: "all" }));
      dispatch(loadAllAgent());
    }
  }, [dispatch, enabled]);

  // Country list
  const countries = COUNTRIES;

  // Nationality list
  const nationalities = NATIONALITIES;

  // Gender options
  const genders = [
    { value: 'male', label: t("common.male") || 'Male' },
    { value: 'female', label: t("common.female") || 'Female' },
  ];

  const filters = [
    // معلومات أساسية
    {
      key: "firstName",
      label: t("lead.firstName") || "First Name",
      type: "text",
    },
    {
      key: "lastName",
      label: t("lead.lastName") || "Last Name",
      type: "text",
    },
    {
      key: "email",
      label: t("common.email"),
      type: "text",
    },
    {
      key: "phone",
      label: t("lead.phoneNumber") || "Phone",
      type: "text",
    },
    {
      key: "mobile",
      label: t("lead.mobile") || "Mobile",
      type: "text",
    },
    {
      key: "country",
      label: t("lead.country") || "Country",
      type: "select",
      mode: "multiple",
      options: countries,
    },
    {
      key: "country_of_study",
      label: t("lead.country_of_study") || "Country of Study",
      type: "select",
      mode: "multiple",
      options: countries,
    },
    {
      key: "nationality",
      label: t("lead.nationality") || "Nationality",
      type: "select",
      mode: "multiple",
      options: nationalities,
    },
    {
      key: "gender",
      label: t("lead.gender") || "Gender",
      type: "select",
      mode: "multiple",
      options: genders,
    },
    {
      key: "dateOfBirth",
      label: t("lead.dateOfBirth") || "Date of Birth",
      type: "dateRange",
    },

    // معلومات Lead
    {
      key: "leadOwnerId",
      label: t("lead.leadOwner") || "Lead Owner",
      loading: staffLoading,
      type: "select",
      mode: "multiple",
      options: (() => {
        const currentList = (canReadAllUser && staffList) ? [...staffList] : [];
        const currentUserId = localStorage.getItem("id");
        const currentUserName = localStorage.getItem("user");

        if (currentUserId && !currentList.some(item => String(item.id) === String(currentUserId))) {
          currentList.push({
            id: parseInt(currentUserId),
            username: currentUserName,
            firstName: "",
            lastName: ""
          });
        }

        return currentList.map((item) => ({
          label: item?.username || (item.firstName || item.lastName ? `${item.firstName} ${item.lastName}` : null) || `User ${item.id}`,
          value: item.id,
        }));
      })(),
    },
    {
      key: "leadSourceId",
      label: t("lead.leadSource") || "Lead Source",
      loading: leadSourcesLoading,
      type: "select",
      mode: "multiple",
      options: leadSources?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || [],
    },
    {
      key: "leadStatus",
      label: t("lead.leadStatus") || "Lead Status",
      loading: leadStatusesLoading,
      type: "select",
      mode: "multiple",
      options: leadStatuses?.map((item) => ({
        label: item.name,
        value: item.name,
      })) || [],
    },
    // Agent filter hidden as requested

    // معلومات أكاديمية
    {
      key: "interest",
      label: t("lead.interest") || "Interest",
      loading: interestsLoading,
      type: "select",
      mode: "multiple",
      options: interests?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || [],
    },
    {
      key: "major",
      label: t("lead.major") || "Major",
      loading: majorsLoading,
      type: "select",
      mode: "multiple",
      options: majors?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || [],
    },
    // معلومات إضافية
    {
      key: "adId",
      label: t("lead.adId") || "Ad ID",
      type: "text",
    },
    {
      key: "campaignCode",
      label: t("lead.campaignCode") || "Campaign Code",
      loading: campaignCodesLoading,
      type: "select",
      mode: "multiple",
      options: campaignCodes?.map((item) => ({
        label: item.name,
        value: item.name,
      })) || [],
    },
    // Removed redundant companyId filter as it's merged into agentId

    // تاريخ الإنشاء والتحديث
    {
      key: "dateRange", // Maps to startDate/endDate in backend (created_at)
      label: t("lead.createdAt"),
      type: "dateRange",
    },
    {
      key: "updatedAtRange", // Maps to updatedAtRange_startDate/endDate in backend (updated_at)
      label: t("lead.updatedAt"),
      type: "dateRange",
    },

    // الحالة العامة
    {
      key: "status",
      label: t("common.status"),
      type: "select",
      mode: "multiple",
      options: [
        { label: t("common.show"), value: "true" },
        { label: t("common.hide"), value: "false" },
      ],
    },
  ];

  return filters;
}
